import React, { createContext, useContext, useCallback, useState, useEffect, useMemo, ReactNode } from "react";
import PocketBase, { RecordModel } from "pocketbase";
import { useInterval } from "usehooks-ts";
import { jwtDecode } from "jwt-decode";
import ms from "ms";
import { OperatorsRecord, TypedPocketBase } from "@/types/pocketbase-types";

const BASE_URL = import.meta.env.PUBLIC_ENV__PB_URL || 'https://pb.medtaxi-774.ru';
const fiveMinutesInMs = ms("5 minutes");
const twoMinutesInMs = ms("2 minutes");

interface User extends OperatorsRecord {
  email: string;
  username: string;
}

interface DecodedToken {
  exp: number;
  [key: string]: any;
}

interface PocketContextType {
  register: (email: string, password: string) => Promise<RecordModel>;
  login: (email: string, password: string) => Promise<RecordModel>;
  logout: () => void;
  refreshSession: () => Promise<void>;
  user: User | null;
  token: string | null;
  pb: TypedPocketBase;
}

const defaultContext: PocketContextType = {
  register: async () => {
    throw new Error("PocketContext not provided");
  },
  login: async () => {
    throw new Error("PocketContext not provided");
  },
  logout: () => {
    throw new Error("PocketContext not provided");
  },
  refreshSession: async () => {
    throw new Error("PocketContext not provided");
  },
  user: null,
  token: null,
  pb: new PocketBase(BASE_URL) as TypedPocketBase,
};

const PocketContext = createContext<PocketContextType>(defaultContext);

interface PocketProviderProps {
  children: ReactNode;
}

export const PocketProvider: React.FC<PocketProviderProps> = ({ children }) => {
  const pb = useMemo(() => new PocketBase(BASE_URL) as TypedPocketBase, []);

  const [token, setToken] = useState<string | null>(pb.authStore.token);
  const [user, setUser] = useState<User | null>(pb.authStore.model as User | null);

  useEffect(() => {
    return pb.authStore.onChange((token, model) => {
      setToken(token);
      setUser(model as User | null);
    });
  }, [pb]);

  const register = useCallback(
    async (email: string, password: string) => {
      return await pb.collection("users").create({
        email,
        password,
        passwordConfirm: password,
      });
    },
    [pb],
  );

  const login = useCallback(
    async (email: string, password: string) => {
      return await pb.collection("operators").authWithPassword(email, password).then();
    },
    [pb],
  );

  const logout = useCallback(() => {
    pb.authStore.clear();
  }, [pb]);

  const refreshSession = useCallback(async () => {
    if (!pb.authStore.isValid) return;

    const decoded = jwtDecode<DecodedToken>(token!);
    const tokenExpiration = decoded.exp;
    const expirationWithBuffer = (tokenExpiration + fiveMinutesInMs) / 1000;

    if (Date.now() / 1000 > expirationWithBuffer) {
      await pb.collection("operators").authRefresh();
    }
  }, [pb, token]);

  useInterval(refreshSession, token ? twoMinutesInMs : null);

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    <PocketContext.Provider value={{ register, login, logout, refreshSession, user, token, pb }}>
      {children}
    </PocketContext.Provider>
  );
};

export function usePocket() {
  const context = useContext(PocketContext);
  if (!context) {
    throw new Error("usePocket must be used within a PocketProvider");
  }

  const logAction = async (applicationId: string, type: "create" | "update" | "view" | "delete") => {
    try {
      if (!context.user) {
        console.error("No user logged in");
        return;
      }

      console.log(`Логирование действия: ${type} для заявки ${applicationId}`);
      
      // Проверяем существование заявки перед логированием
      const appExists = await context.pb.collection("applications").getOne(applicationId)
        .then(() => true)
        .catch(() => false);
        
      if (!appExists) {
        console.error(`Заявка ${applicationId} не найдена`);
        return;
      }
      
      const result = await context.pb.collection("events").create({
        application: applicationId,
        operator: context.user.id,
        type,
      });
      
      console.log("Событие успешно записано:", result);
      
    } catch (error) {
      console.error("Failed to log action:", error);
    }
  };

  return { ...context, logAction };
}
